import { remarkPluginsCtx } from "@milkdown/core";
import { $node } from "@milkdown/utils";

// Splits a text string into mention and text nodes for the MDAST tree.
function splitMentions(text: string): object[] {
  const re = /@\{(\d+)\}/g;
  const result: object[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    result.push({ type: "mention", userId: parseInt(match[1], 10) });
    lastIndex = re.lastIndex;
  }

  if (lastIndex < text.length) {
    result.push({ type: "text", value: text.slice(lastIndex) });
  }

  return result;
}

type MdastNode = { type?: string; children?: MdastNode[]; value?: string };

function visitNode(node: MdastNode): void {
  if (!node.children) return;
  const newChildren: MdastNode[] = [];
  for (const child of node.children) {
    if (child.type === "text" && child.value) {
      const parts = splitMentions(child.value);
      if (parts.length > 1 || (parts.length === 1 && (parts[0] as { type?: string }).type === "mention")) {
        newChildren.push(...(parts as MdastNode[]));
        continue;
      }
    }
    visitNode(child);
    newChildren.push(child);
  }
  node.children = newChildren;
}

// Remark plugin: transforms @[id] text patterns into mention MDAST nodes.
export function remarkMentions() {
  return (tree: MdastNode) => {
    visitNode(tree);
  };
}

// ProseMirror $node plugin for inline mention atoms stored as @[userId].
export const mentionNode = $node("mention", ctx => {
  ctx.update(remarkPluginsCtx, plugins => [...plugins, { plugin: remarkMentions, options: {} }]);

  return {
    group: "inline",
    inline: true,
    atom: true,
    selectable: false,
    attrs: { userId: { default: 0 } },
    toDOM(node: { attrs: { userId: number } }) {
      return ["span", { class: "mention-pill", "data-mention-id": String(node.attrs.userId) }, `@${node.attrs.userId}`];
    },
    parseDOM: [
      {
        tag: "span[data-mention-id]",
        getAttrs(dom: HTMLElement | string) {
          if (typeof dom === "string") return false;
          const id = parseInt(dom.getAttribute("data-mention-id") || "0", 10);
          return { userId: id };
        },
      },
    ],
    parseMarkdown: {
      match: (node: { type: string }) => node.type === "mention",
      runner: (state: { addNode: (type: object, attrs: object) => void }, node: { userId: number }, type: object) => {
        state.addNode(type, { userId: node.userId });
      },
    },
    toMarkdown: {
      match: (node: { type: { name: string } }) => node.type.name === "mention",
      runner: (
        state: { addNode: (type: string, children?: undefined, value?: string) => void },
        node: { attrs: { userId: number } },
      ) => {
        state.addNode("text", undefined, `@{${node.attrs.userId}}`);
      },
    },
  };
});
